import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle, FileText, CreditCard, Printer, Clock, Phone, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import SEOHead from '@/components/seo/SEOHead';
import { 
  createFAQPageSchema, 
  createBreadcrumbListSchema, 
  createSpeakable 
} from '@/utils/schemaGenerator';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div 
      className="border border-gray-200 rounded-xl bg-white overflow-hidden"
      data-testid="faq-item"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-gray-50 transition-colors"
        data-testid="faq-toggle"
      >
        <span className="font-medium text-gray-900 text-sm sm:text-base pr-4">
          {question}
        </span>
        <span className="flex-shrink-0 text-gray-400">
          {isOpen ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </span>
      </button>
      {isOpen && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0">
          <p className="text-gray-600 text-sm leading-relaxed">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
};

const FAQSection = ({ title, number, questions, openItems, onToggle }) => {
  return (
    <div className="mb-8" data-testid={`faq-section-${number}`}>
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
        {number}. {title}
      </h2>
      <div className="space-y-3">
        {questions.map((item, index) => (
          <FAQItem
            key={`${number}-${item.q.substring(0, 20)}`}
            question={item.q}
            answer={item.a}
            isOpen={openItems[`${number}-${index}`]}
            onToggle={() => onToggle(`${number}-${index}`)}
          />
        ))}
      </div>
    </div>
  );
};

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (key) => {
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const faqs = [
    {
      title: 'Uploading Files',
      questions: [
        {
          q: 'What file formats are supported?',
          a: 'We support PDF, DOC, DOCX, and TXT files. PDF files work best for accurate printing.'
        },
        {
          q: 'What is the maximum file size?',
          a: 'You can upload files up to 50MB in size. For larger files, please compress them and then upload.'
        },
        {
          q: 'Can I upload multiple files?',
          a: 'Currently, we support single file uploads per print job. For multiple documents, please create separate print jobs for each file.'
        },
        {
          q: 'How do I know how many pages are there in my file(s)?',
          a: 'Once your file is uploaded, the system will automatically detect and display the total number of pages.'
        }
      ]
    },
    {
      title: 'Pricing',
      questions: [
        {
          q: 'What are the printing rates?',
          a: 'A4 Size: Black & White ₹2/page, Color ₹10/page. A3 Size: Black & White ₹5/page, Color ₹20/page.'
        },
        {
          q: 'Is there any extra charge?',
          a: 'No extra charges. The cost is simply: (Number of Pages) × (Number of Copies) × (Rate per page).'
        },
        {
          q: 'What payment methods are accepted?',
          a: 'We accept all major payment methods through Razorpay including UPI, Credit/Debit Cards, Net Banking, and Wallets.'
        },
        {
          q: 'What if the payment fails?',
          a: 'If the payment fails, no amount will be deducted and you can retry the payment process.'
        }
      ]
    },
    {
      title: 'Printing Process',
      questions: [
        {
          q: 'How long does printing take?',
          a: 'Most print jobs complete within 1-2 minutes. During busy hours, there may be a short queue.'
        },
        {
          q: 'What paper sizes are available?',
          a: 'We offer A4 and A3 paper sizes.'
        },
        {
          q: 'Can I print double-sided?',
          a: 'Yes! Enable the "Print on Both Sides" option in the preferences to save paper.'
        },
        {
          q: 'What if my document doesn\'t print correctly?',
          a: 'Contact the store staff immediately. If there\'s a printing error on our end, we\'ll reprint your document at no extra cost.'
        }
      ]
    },
    {
      title: 'Queue & Timing',
      questions: [
        {
          q: 'How do I know when my print is ready?',
          a: 'After successful payment, your document is automatically sent to the printer. The estimated wait time is shown on your screen.'
        },
        {
          q: 'Can I cancel my print job?',
          a: 'Once payment is completed, the print job is immediately sent to the printer and cannot be cancelled.'
        }
      ]
    },
    {
      title: 'Support',
      questions: [
        {
          q: 'How can I contact support?',
          a: 'For immediate assistance, please speak to the store staff. For other queries, WhatsApp us or email at support@inchpaper.com'
        },
        {
          q: 'I paid but my document didn\'t print. What should I do?',
          a: 'Please inform the store staff immediately with your Job ID (shown on the confirmation screen). They will help resolve the issue.'
        }
      ]
    }
  ];

  const allFlatFaqs = faqs.flatMap(sec => sec.questions);
  const faqSchema = createFAQPageSchema(allFlatFaqs);
  const breadcrumbSchema = createBreadcrumbListSchema([
    { name: "Home", url: "/" },
    { name: "FAQ", url: "/faq" }
  ]);

  return (
    <div className="min-h-screen bg-white flex flex-col justify-between">
      <SEOHead
        title="Frequently Asked Questions (FAQ) | Inchpaper Print"
        description="Find answers to common questions about online PDF document printing, paper sizes, UPI payments, and self-service print kiosks."
        keywords="inchpaper faq, online printing questions, kiosk printing help, payment print refund"
        canonical="https://print.inchpaper.com/faq"
        schemas={[
          faqSchema,
          breadcrumbSchema,
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Help & FAQs",
            "speakable": createSpeakable(["h1", "h2"])
          }
        ]}
      />
      <div>
        {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/">
            <img 
              src="/inchpaper-printezy.png" 
              alt="Inchpaper" 
              className="h-8 sm:h-10 w-auto object-contain"
              onError={(e) => { e.target.src = '/inchpaper-logo.png'; }}
            />
          </Link>
          <Link 
            to="/faq" 
            className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-full text-gray-700"
            data-testid="help-btn-faq"
          >
            <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-sm sm:text-base font-medium">Help</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <div className="py-8 sm:py-10">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Help & FAQs
          </h1>
          <p className="text-gray-500 text-sm sm:text-base">
            Find answers to common questions or contact support
          </p>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-2xl mx-auto px-4 pb-24">
        {faqs.map((section, sectionIndex) => (
          <FAQSection
            key={`section-${section.title}`}
            title={section.title}
            number={sectionIndex + 1}
            questions={section.questions}
            openItems={openItems}
            onToggle={toggleItem}
          />
        ))}

        {/* Contact Section */}
        <div className="mt-10 text-center p-6 bg-gray-50 rounded-xl">
          <h2 className="font-semibold text-gray-900 mb-2">Still have questions?</h2>
          <p className="text-gray-600 text-sm mb-4">
            Our team is here to help you
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a 
              href="https://wa.me/917703960982"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
              data-testid="whatsapp-support-btn"
            >
              WhatsApp Us
            </a>
            <Link to="/">
              <Button variant="outline" className="w-full sm:w-auto" data-testid="start-printing-btn">
                Start Printing
              </Button>
            </Link>
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default FAQPage;
